import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import Iframe from 'react-iframe';
import {disableWebboard} from 'store/media'; // Import the action to clear webboard
import {
  setCourseData,
  setCurrentLessonProgress,
  fetchCourseData,
  setShowUnitSelectionScreen,
} from '../../store/course';
import {
  API_KEY,
  QUIKLEARN_BACKEND_URL,
  TOKEN_GENERATION_API,
} from 'config/app.config';
import {getQuikcoinsBalance} from 'utils/quikcoinsService';

const parseRecommendedUnitNumber = recommendedUnit => {
  if (!recommendedUnit) return null;
  const s = String(recommendedUnit);
  const m = /unit\s*(\d+)/i.exec(s);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const WebboardService = () => {
  const webboardUrl = useSelector(state => state.media.webboardUrl);
  const courseData = useSelector(state => state.course.courseData);
  const curriculumList = useSelector(state => state.course.curriculumList);
  const curriculumGS = useSelector(state => state.course.curriculumGradeAndSubject);
  const userId = useSelector(state => state.app?.courseTokenValues?.userId);
  const token = useSelector(state => state.meeting?.properties?.token);
  const avatarUrl = useSelector(state => state.meeting?.meeting?.avatarUrlValue);
  const dispatch = useDispatch();
  const [showWebboard, setShowWebboard] = useState(false);
  const [pendingQuizCompletion, setPendingQuizCompletion] = useState(null);

  // Always show the iframe when a new webboard URL is received
  useEffect(() => {
    if (webboardUrl) {
      setShowWebboard(true);
    }
  }, [webboardUrl]);

  // Hide and clear the webboard when the close button is clicked
  const hideWebboard = () => {
    setShowWebboard(false);
    dispatch(disableWebboard()); // Clear the webboard URL from Redux state
  };

  const syncCoinsBestEffort = async (reason = 'Assessment') => {
    try {
      if (!userId) return;
      const response = await getQuikcoinsBalance({userId, token});
      const balance =
        response?.data?.balance ??
        response?.balance ??
        response?.data?.newBalance ??
        response?.newBalance;
      const numeric = Number(balance);
      if (!Number.isFinite(numeric)) return;

      // Best-effort: store + emit an event (Meeting can listen if desired)
      try {
        window.localStorage?.setItem('quikcoinsBalance', String(numeric));
      } catch (_) {}
      try {
        window.dispatchEvent(
          new CustomEvent('quikcoins:balance', {
            detail: {balance: numeric, reason},
          }),
        );
      } catch (_) {}
    } catch (_) {}
  };

  const generateMetaverseToken = async ({curriculumId, unitId}) => {
    const base = String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');
    const endpoint = String(TOKEN_GENERATION_API || '');
    const url = `${base}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({
        userId,
        curriculumId,
        unitId,
      }),
    });
    if (!response.ok) throw new Error('Failed to generate metaverse token');
    const data = await response.json();
    return data?.token;
  };

  const launchRecommendedUnitFromQuizCompletionWeb = async ({
    mode,
    recommendedUnit,
  } = {}) => {
    if (mode !== 'diagnostic') return;
    if (!userId) throw new Error('Missing userId');
    const unitNumber = parseRecommendedUnitNumber(recommendedUnit);
    if (!unitNumber) throw new Error('Invalid recommendedUnit');

    const units = courseData?.units || [];
    const unit =
      units.find(u => Number(u.unitNumber) === Number(unitNumber)) ||
      units[unitNumber - 1] ||
      null;
    if (!courseData?.curriculumId) throw new Error('Missing curriculumId');
    if (!unit?.id) throw new Error('Could not resolve unit from recommendedUnit');

    // Preset first lesson context (mirrors UnitSelectionOverlay)
    try {
      const firstLesson = unit?.lessons?.[0];
      if (firstLesson) {
        dispatch(
          setCourseData({
            ...courseData,
            currentLessonId: firstLesson.lessonId,
            currentLessonTotalSlides: firstLesson.totalSlides,
          }),
        );
        dispatch(
          setCurrentLessonProgress({
            currentLessonId: firstLesson.lessonId,
            currentLessonProgressPercentage: Number(firstLesson.progress) || 0,
            currentLessonTotalSlides: Number(firstLesson.totalSlides) || 0,
            currentLessonMasteryScore: Number(firstLesson.masteryScore) || 0,
          }),
        );
      }
    } catch (_) {}

    const metaverseToken = await generateMetaverseToken({
      curriculumId: courseData.curriculumId,
      unitId: unit.id,
    });
    if (!metaverseToken) throw new Error('No token returned');

    const safeAvatar = encodeURIComponent(String(avatarUrl || ''));
    window.location.href = `${window.location.origin}/?token=${metaverseToken}&avatarSelection=false&legacy=false&avatarUrl=${safeAvatar}`;
  };

  // If quizCompletion arrives before courseData is loaded, we stash it and retry
  // once courseData becomes available.
  useEffect(() => {
    if (!pendingQuizCompletion) return;
    if (!courseData) return;

    (async () => {
      try {
        await launchRecommendedUnitFromQuizCompletionWeb(pendingQuizCompletion);
      } catch (e) {
        // If we can't launch, fall back to unit selection so the user can proceed.
        try {
          dispatch(setShowUnitSelectionScreen(true));
        } catch (_) {}
      } finally {
        setPendingQuizCompletion(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseData, pendingQuizCompletion]);

  const handleLessonMasteryAchieved = data => {
    try {
      if (!courseData || !courseData.units || !data?.unitId || !data?.lessonId) {
        return;
      }

      const updatedUnits = (courseData.units || []).map(unit => {
        if (unit.id !== data.unitId) return unit;

        const updatedLessons = (unit.lessons || []).map(lesson => {
          if (lesson.lessonId !== data.lessonId) return lesson;

          const existingProgress =
            typeof lesson.progress === 'number'
              ? lesson.progress
              : typeof lesson.progressValue === 'number'
                ? lesson.progressValue
                : 0;

          return {
            ...lesson,
            // Do NOT force lesson progress to 100 here; it should reflect actual
            // video/slide completion. Only mark mastery status.
            progress: existingProgress,
            isMastered: true,
          };
        });

        const totalLessons = updatedLessons.length;
        const lessonsMastered = updatedLessons.filter(l => l.isMastered).length;
        const unitMasteryPercentage =
          totalLessons > 0 ? Math.round((lessonsMastered / totalLessons) * 100) : 0;
        const lessonsCompletedPercentage =
          totalLessons > 0
            ? Math.round(
                updatedLessons.reduce((acc, l) => acc + (l.progress || 0), 0) /
                  totalLessons,
              )
            : 0;
        const hundredPercentCompletedCount = updatedLessons.filter(
          l => l.progress === 100,
        ).length;

        return {
          ...unit,
          lessons: updatedLessons,
          totalLessons,
          lessonsMastered,
          unitMasteryPercentage,
          lessonsCompletedPercentage,
          hundredPercentCompletedCount,
        };
      });

      const totalUnits = updatedUnits.length;
      const LESSON_MASTERY_THRESHOLD = 90;
      const unitsMastered = updatedUnits.filter(
        u => (u.unitMasteryPercentage ?? 0) >= LESSON_MASTERY_THRESHOLD,
      ).length;
      const calculatedCourseMastery =
        totalUnits > 0 ? Math.round((unitsMastered / totalUnits) * 100) : 0;

      const updatedCourseData = {
        ...courseData,
        units: updatedUnits,
        courseMastery: calculatedCourseMastery,
      };

      dispatch(setCourseData(updatedCourseData));
    } catch (_) {}
  };

  useEffect(() => {
    const handleMessage = event => {
      if (!event?.data) return;

      // Log every event we receive

      // webboard sometimes posts objects, sometimes JSON strings
      let data = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (_) {
          // keep as-is
        }
      }

      if (data && data.type) {
        // Helpful debug log (can be removed later)
        console.log('[WebboardService] Received event:', data);
        if (data.type === 'updatedLessonMasteryScore') {
          dispatch(
            setCurrentLessonProgress({
              currentLessonMasteryScore: data.score,
            }),
          );
          syncCoinsBestEffort('Lesson Mastery');
        } else if (data.type === 'quizCompletion') {
          (async () => {
            try {
              // Match mobile behavior: attempt to directly launch recommended unit (web redirect)
              if (courseData) {
                await launchRecommendedUnitFromQuizCompletionWeb({
                  mode: data.mode,
                  recommendedUnit: data.recommendedUnit,
                });
              } else {
                // Trigger course data load and retry once it arrives.
                const grade = curriculumGS?.grade;
                const subject = curriculumGS?.subject;
                if (token && grade && subject) {
                  try {
                    await dispatch(
                      fetchCourseData({
                        token,
                        worldToLoad: 'quikbase',
                        curriculumList: curriculumList ?? null,
                        isTokenBasedFetching: false,
                        grade,
                        subject,
                        customCourse: false,
                      }),
                    );
                  } catch (_) {}
                }
                setPendingQuizCompletion({
                  mode: data.mode,
                  recommendedUnit: data.recommendedUnit,
                });
              }
            } catch (_) {
              // If we can't launch, fall back to unit selection so the user can proceed.
              try {
                dispatch(setShowUnitSelectionScreen(true));
              } catch (_) {}
            }
          })();
        } else if (data.type === 'assessmentQuestionSubmitted') {
          // Web-only branch: message recognized (analytics handled elsewhere / not wired here).
        } else if (data.type === 'assessmentSessionSummary') {
          // Web-only branch: message recognized (analytics handled elsewhere / not wired here).
        } else if (data.type === 'lessonMasteryAchieved') {
          handleLessonMasteryAchieved(data);
        } else if (data.type === 'disableWebboard') {
          hideWebboard();
          syncCoinsBestEffort('Assessment');
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [courseData, dispatch, hideWebboard]);

  return (
    <>
      {showWebboard && webboardUrl && (
        <div
          style={{
            position: 'absolute',
            top: '0px',
            left: '0px',
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            zIndex: 100,
          }}>
          {/* Close Button in Top Right */}
          <button
            onClick={hideWebboard}
            style={{
              position: 'absolute',
              top: '20px',
              right: '10px',
              width: '40px',
              height: '40px',
              fontSize: '20px',
              fontWeight: 'bold',
              color: 'black',
              backgroundColor: '#C5E836',
              borderRadius: 10,
              cursor: 'pointer',
            }}>
            ✖
          </button>

          {/* Embedded Webboard */}
          <Iframe
            url={webboardUrl}
            width="100%"
            height="100%"
            id="webboard-iframe"
            style={{position: 'relative', border: 'none'}}
          />
        </div>
      )}
    </>
  );
};
